import React from "react";
import { useTranslation } from 'react-i18next';
import searchPnc from "../../assets/searchPnc.svg";
import "./UserMessage.css";

const UserMessage = ({ message, cycleId, id }) => {
  const { t, i18n } = useTranslation();
  if (!message) return null;

  const getMonthName = (monthInput) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthNum = parseInt(monthInput);
    if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
      return monthNames[monthNum - 1];
    }
    return monthInput;
  };

  // Generic parser: extracts any **Key** Value or **Key**: Value lines and the trailing query
  const parseFormattedText = (text) => {
    // Literal \n (written as \\n in JSON) marks the boundary between selections and user query
    const lastLiteralNl = text.lastIndexOf("\\n");
    const selectionText = lastLiteralNl !== -1 ? text.substring(0, lastLiteralNl) : text;
    let queryText = lastLiteralNl !== -1 ? text.substring(lastLiteralNl + 2).trim() : "";

    // Parse each line for **Label** Value or **Label**: Value
    const rawPairs = [];
    for (const line of selectionText.split("\n")) {
      const match = line.match(/^\*\*(.+?)\*\*:?\s*(.*)/);
      if (match) rawPairs.push({ label: match[1].trim(), value: match[2].trim() });
    }

    // Find positions and values of Month / Year for Calendar merging
    let firstCalendarRelatedIdx = -1;
    let monthValue = "";
    let yearValue = "";
    let hasDirectCalendar = false;

    rawPairs.forEach((pair, i) => {
      const ll = pair.label.toLowerCase();
      if (/^calend?ar$/.test(ll)) {
        hasDirectCalendar = true;
      } else if (ll === "month" || ll === "year") {
        if (ll === "month") monthValue = pair.value;
        if (ll === "year") yearValue = pair.value;
        if (firstCalendarRelatedIdx === -1) firstCalendarRelatedIdx = i;
      }
    });

    // Build result: merge Month + Year into Calendar at their first position
    // Only merge into Calendar when both Month and Year are present and non-empty
    const shouldMergeCalendar = !hasDirectCalendar && monthValue && yearValue;

    const result = [];
    let calendarInserted = hasDirectCalendar;

    for (let i = 0; i < rawPairs.length; i++) {
      const pair = rawPairs[i];
      const ll = pair.label.toLowerCase();

      if (shouldMergeCalendar && !calendarInserted && i === firstCalendarRelatedIdx) {
        const monthName = getMonthName(monthValue);
        result.push({ label: "Calendar", value: `${monthName} ${yearValue}` });
        calendarInserted = true;
      }

      // Skip Month/Year only when they are merged into Calendar
      if ((ll === "month" || ll === "year") && shouldMergeCalendar) continue;
      // When not merging, keep them as regular fields (empty values will be filtered later)
      if (ll === "month" || ll === "year") { result.push(pair); continue; }

      if (/^calend?ar$/.test(ll)) {
        const parts = pair.value.split(/\s+/);
        if (parts.length > 0) parts[0] = getMonthName(parts[0]);
        result.push({ label: pair.label, value: parts.join(" ") });
      } else {
        result.push(pair);
      }
    }

    // Drop entries with empty values
    const selections = result.filter(p => p.value);

    // Fallback: find query as the last non-** line when no literal \n separator
    if (!queryText) {
      const allLines = text.split(/\\n|\n/);
      for (let i = allLines.length - 1; i >= 0; i--) {
        const line = allLines[i].trim();
        if (line && !line.startsWith("**")) { queryText = line; break; }
      }
    }

    return { selections, queryText };
  };

  // Translate known field labels; fall back to the raw label from the server
  const fieldLabelMap = {
    "vin": t('vin'),
    "vin/chassis": t('vin'),
    "vehicle type": t('vehicleType') || "Vehicle Type",
    "model": t('modelName'),
    "country": t('country'),
    "year": t('Year'),
    "month": t('Month'),
    "calendar": t('calendar'),
    "calander": t('calendar'),
    "publication name": t('publicationNumber'),
    "publication number": t('publicationNo'),
    "classification": t('classificationShop'),
  };

  const getTranslatedLabel = (label) => fieldLabelMap[label.toLowerCase()] || label;

  const getTranslatedValue = (label, value) => {
    const ll = label.toLowerCase();
    if (ll === "country") return t(value);
    if (/^calend?ar$/.test(ll)) {
      const parts = value.split(" ");
      return parts.length >= 2 ? `${t(parts[0])} ${parts.slice(1).join(" ")}` : value;
    }
    if (ll === "publication name") return t(value);
    return value;
  };

  const colorPairs = [
    { labelBg: "bg-[#FFF1F1]", valueBg: "bg-[#FFFFFF]" },
    { labelBg: "bg-[#FFE2E2]", valueBg: "bg-[#EDEDED]" },
  ];

  const renderSelections = (selections, roundBottomCorners) => (
    <div className="inputPartPnc">
      <div className="inOptionPnc">
        {selections.map((item, index) => {
          const colorPair = colorPairs[index % 2];
          const isFirst = index === 0;
          const isLast = index === selections.length - 1;
          return (
            <div className="vinOptionPnc" key={index}>
              <div
                className={`label vinLabelPnc ${colorPair.labelBg} px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""} ${isLast && roundBottomCorners ? "rounded-bl-lg" : ""}`}
              >
                {getTranslatedLabel(item.label)}
              </div>
              <div
                className={`value ${colorPair.valueBg} px-2 py-1 ${isFirst ? "rounded-tr-lg" : ""} ${isLast && roundBottomCorners ? "rounded-br-lg" : ""}`}
              >
                {getTranslatedValue(item.label, item.value) || "N/A"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // History messages
  if (message.messageId && message.messageId.includes("history")) {
    const { selections, queryText } = parseFormattedText(message.text);

    return (
      <div
        className="bg-gray-100 rounded-lg ml-auto max-w-[500px] userPartPnc"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={queryText}
      >
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
            </div>
          </div>
          {renderSelections(selections, false)}
        </div>
        <div className="questionPnc">
          <div className="questionIcoPnc"></div>
          <div>
            <p className="questionPartPnc">{queryText || message.text}</p>
          </div>
        </div>
      </div>
    );
  }

  // Live chat messages that contain formatted **Key** Value pairs
  if (message.text && message.text.includes("**") && !message.searchInfo) {
    const { selections, queryText } = parseFormattedText(message.text);

    if (selections.length > 0) {
      return (
        <div
          className="bg-gray-100 rounded-lg ml-auto max-w-[500px] userPartPnc"
          id={message.messageId}
          data-message-id={message.messageId}
          data-message-text={queryText || message.text}
        >
          <div className="queryOptionsPnc">
            <div className="selectionsPnc">
              <div className="selectWithIcon">
                <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
              </div>
            </div>
            {renderSelections(selections, true)}
          </div>
          <div className="questionPnc">
            <div className="questionIcoPnc"></div>
            <div>
              <p className="questionPartPnc">{queryText || message.text}</p>
            </div>
          </div>
        </div>
      );
    }
  }

  if (message.searchInfo) {
    const hasVin = message.searchInfo.vin && message.searchInfo.vin !== "N/A";
    const currentLanguage = i18n?.language || localStorage.getItem('selectedLanguage') || 'jp';

    const itemsToDisplay = [];

    if (hasVin) {
      itemsToDisplay.push({ label: t('vin'), value: message.searchInfo.vin });
    }

    if (message.searchInfo.vehicleType && message.searchInfo.vehicleType !== "N/A") {
      itemsToDisplay.push({ label: t('vehicleType') || "Vehicle Type", value: message.searchInfo.vehicleType });
    }

    if (message.searchInfo.model && (!hasVin || message.searchInfo.model !== "N/A")) {
      itemsToDisplay.push({ label: t('modelName'), value: message.searchInfo.model });
    }

    if (message.searchInfo.country && message.searchInfo.country !== "N/A") {
      let countryDisplayName = message.searchInfo.country;
      if (currentLanguage === 'jp' && message.searchInfo.countryJP) {
        countryDisplayName = message.searchInfo.countryJP;
      } else if (currentLanguage === 'en' && message.searchInfo.countryEN) {
        countryDisplayName = message.searchInfo.countryEN;
      }
      itemsToDisplay.push({ label: t('country'), value: countryDisplayName });
    }

    if (
      (message.searchInfo.calendar && message.searchInfo.calendar !== "N/A") ||
      (message.searchInfo.calander && message.searchInfo.calander !== "N/A")
    ) {
      let calendarValue = message.searchInfo.calendar || message.searchInfo.calander;
      const parts = calendarValue.split(/\s+/);
      if (parts.length > 0) {
        parts[0] = t(getMonthName(parts[0]));
        calendarValue = parts.join(" ");
      }
      itemsToDisplay.push({ label: t('calendar'), value: calendarValue });
    } else if (message.searchInfo.month && message.searchInfo.year) {
      const monthValue = t(getMonthName(message.searchInfo.month));
      itemsToDisplay.push({ label: t('calendar'), value: `${monthValue} ${message.searchInfo.year}` });
    } else if (message.searchInfo.year) {
      itemsToDisplay.push({ label: t('Year'), value: message.searchInfo.year });
    }

    if (message.searchInfo.publicationName && message.searchInfo.publicationName !== "N/A") {
      itemsToDisplay.push({ label: t('publicationNumber'), value: t(message.searchInfo.publicationName) });
    }

    if (message.searchInfo.publicationNumber && message.searchInfo.publicationNumber !== "N/A") {
      itemsToDisplay.push({ label: t('publicationNo'), value: message.searchInfo.publicationNumber });
    }

    if (message.searchInfo.classification && (!hasVin || message.searchInfo.classification !== "N/A")) {
      itemsToDisplay.push({ label: "Classification", value: message.searchInfo.classification });
    }

    if (message.searchInfo.mainGroup) {
      itemsToDisplay.push({ label: "Main Group", value: message.searchInfo.mainGroup });
    }
    if (message.searchInfo.subGroup) {
      itemsToDisplay.push({ label: "Sub Group", value: message.searchInfo.subGroup });
    }

    return (
      <div
        className="bg-gray-100 ml-auto max-w-[500px] userPartPnc custom-radius"
        id={message.messageId}
        data-message-id={message.messageId}
        data-message-text={message.text}
      >
        <div className="queryOptionsPnc">
          <div className="selectionsPnc">
            <div className="selectWithIcon">
              <img src={searchPnc} alt={t('findAnything')} /> {t('selectionSummary')}:
            </div>
          </div>
          <div className="inputPartPnc">
            <div className="inOptionPnc">
              {itemsToDisplay.map((item, index) => {
                const colorPair = colorPairs[index % 2];
                const isFirst = index === 0;
                const isLast = index === itemsToDisplay.length - 1;
                return (
                  <div className="vinOptionPnc" key={index}>
                    <div
                      className={`label vinLabelPnc ${colorPair.labelBg} px-2 py-1 ${isFirst ? "rounded-tl-lg" : ""} ${isLast ? "rounded-bl-lg" : ""}`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`value ${colorPair.valueBg} px-2 py-1 ${isFirst ? "rounded-tr-lg" : ""} ${isLast ? "rounded-br-lg" : ""}`}
                    >
                      {item.value || "N/A"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="questionPnc">
          <div className="questionIcoPnc"></div>
          <div>
            <p className="questionPartPnc">{message.text}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-100 rounded-lg ml-auto max-w-[80%] userPartPnc"
      id={message.messageId}
      data-message-id={message.messageId}
      data-message-text={message.text}
    >
      <div className="questionPnc">
        <div className="questionIcoPnc"></div>
        <div>
          <p className="questionPartPnc">{message.text}</p>
        </div>
      </div>
    </div>
  );
};

export default UserMessage;
